import { Suspense, useCallback, useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { RouteFallback } from '@/components/lazyRoute';
import { cn } from '@/components/ui';
import { auditorMediaQueries } from '@/config';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import Drawer from '@/layout/MainLayout/Drawer';
import Header from '@/layout/MainLayout/Header';
import { usePageHeaderStore } from '@/layout/pageHeader';
import { useMenuBar } from '@/services/MenuBarService';
import { NavItemType, NavItemTypeType } from '@/types/menu';

import { CapabilitiesOverrideDrawer } from './MainLayout/Header/HeaderContent/Profile/CapabilitiesOverrideDrawer';
import { useNavbarMenuItems } from './NavbarMenuItems';

export const Page = ({ fullWidth = false }: { fullWidth?: boolean }) => {
  const matchDownLG = useMediaQuery(auditorMediaQueries.downXl);
  const compact = useMediaQuery(auditorMediaQueries.downLg);
  const phone = useMediaQuery(auditorMediaQueries.downSm);
  const drawerOpen = useMenuBar(state => state.drawerOpen);
  const setDrawerOpen = useMenuBar(state => state.setDrawerOpen);
  const { pathname } = useLocation();
  const pageHeader = usePageHeaderStore(state => state.header);
  // Keep page offset tied to the same tokens as `w-drawer` / `w-mini-drawer`.
  // Phones have no icon rail, so the page uses the full width.
  const mainOffset = phone ? '0px' : drawerOpen && !compact ? 'var(--width-drawer)' : 'var(--width-mini-drawer)';

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  useEffect(() => {
    setDrawerOpen(!matchDownLG);
  }, [matchDownLG, setDrawerOpen]);

  const customLocation = location.pathname;

  const getSelectedNavbarItem = useCallback(
    (menu: NavItemType): NavItemType | null => {
      if (menu.children) {
        for (const child of menu.children) {
          if (child.type === 'collapse') {
            const found = getSelectedNavbarItem(child as { children: NavItemType[]; type?: NavItemTypeType });
            if (found) {
              return found;
            }
            if (child.url && customLocation === child.url.split('?')[0]) {
              return child;
            }
          } else if (child.type === 'item') {
            const childUrlPath = child.url?.split('?')[0];
            if (customLocation === childUrlPath) {
              return child;
            }
            // Detail routes that opt in (e.g. /findings/:id) keep their section's title and highlight.
            if (child.matchNested && childUrlPath && customLocation.startsWith(childUrlPath + '/')) {
              return child;
            }
          }
        }
      }
      return null;
    },
    [customLocation],
  );

  const navbarMenuItems = useNavbarMenuItems();
  const currentNavbarItem = useMemo(() => {
    if (!navbarMenuItems?.items) {
      return null;
    }

    for (const menu of navbarMenuItems.items) {
      if (menu.type === 'group') {
        const found = getSelectedNavbarItem(menu as { children: NavItemType[]; type?: NavItemTypeType });
        if (found) {
          return found;
        }
      }
    }
    return null;
  }, [navbarMenuItems, getSelectedNavbarItem]);

  return (
    <div className="flex w-full bg-canvas text-ink">
      <a
        href="#main"
        className="absolute left-4 top-4 z-menu -translate-y-24 rounded-auditor bg-surface px-3 py-2 text-sm font-medium text-ink shadow-auditor focus-visible:translate-y-0 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand"
      >
        Skip to content
      </a>
      <Header
        open={drawerOpen}
        handleDrawerToggle={handleDrawerToggle}
        title={pageHeader.title ?? currentNavbarItem?.title}
        subtitle={pageHeader.subtitle}
        breadcrumb={pageHeader.breadcrumb}
        actions={pageHeader.actions}
      />
      <Drawer open={drawerOpen} handleDrawerToggle={handleDrawerToggle} />
      <main
        id="main"
        // Focusable so the skip link moves focus here, not only the scroll position.
        tabIndex={-1}
        className="min-w-0 flex-1 p-4 transition-overlap duration-300 ease-drawer focus:outline-hidden motion-reduce:transition-none sm:p-6"
        // On compact screens the open drawer overlays the page instead of pushing it.
        style={{ marginLeft: mainOffset }}
      >
        <div
          className={cn(
            'relative flex min-h-page min-w-0 flex-col pb-12 pt-14 sm:px-2',
            fullWidth ? 'max-w-none' : 'mx-auto w-full max-w-screen-xl',
          )}
        >
          <ErrorBoundary resetKey={pathname}>
            <Suspense fallback={<RouteFallback />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </div>
      </main>
      <CapabilitiesOverrideDrawer open={drawerOpen} />
    </div>
  );
};
